import { useOneButtonStore } from '../../../../store/oneButtonStore';
import { useViewerStore } from '../../../../store/store';
import { isPartnerSyncCompleteForConfig } from './partnerSyncConfig';
import { computeCommerceGuide } from './commerceGuideLogic';

function summarizeOpportunityProduct(raw: unknown): string {
    const arr = Array.isArray(raw) ? raw : [];
    const active = arr.filter((p: any) => p?.ActiveFlag !== false && p?.activeFlag !== false);
    if (!active.length) return 'No active opportunity product rows.';
    return active
        .slice(0, 12)
        .map((p: any) => {
            const pt = String(p.Product_Type_Name ?? p.product_Type_Name ?? '').trim() || '?';
            const fp = String(
                p.Finance_Partner_Display_Name ?? p.FinancePartnerDisplayName ?? p.Finance_Partner ?? ''
            ).trim();
            const fm = String(p.Finance_Method_Name ?? p.FinanceMethod ?? '').trim();
            return `${pt}: partner ${fp || '—'}, method ${fm || '—'}`;
        })
        .join('\n');
}

function summarizeSync(status: Record<string, any>): string {
    const keys = Object.keys(status);
    if (!keys.length) return 'No partner sync checklist data stored yet.';
    return keys
        .slice(0, 12)
        .map((k) => {
            const st = status[k];
            const ok = isPartnerSyncCompleteForConfig(k, st);
            return `${k}: ${ok ? 'complete' : 'incomplete'} (lead action: ${st?.leadSyncAction ?? 'none'})`;
        })
        .join('\n');
}

/** Compact, current snapshot for the in-app assistant (no full raw JSON dump). */
export function buildOpportunityAssistantContextFromStore(): string {
    const s = useOneButtonStore.getState();
    const o = s.opportunityDetails;
    const guide = computeCommerceGuide({
        selectedFinanceCells: s.selectedFinanceCells,
        financePartnerSyncStatus: s.financePartnerSyncStatus,
        opportunityDetails: o,
        dashboardQuotesSummaryLines: s.dashboardQuotesSummaryLines,
        changeOrderDetails: s.changeOrderDetails,
    });

    const lines: string[] = [];
    lines.push('## Application context (oneDRAW / Trinity Solar)');
    lines.push(
        `Numeric opportunity id (Trinity / contract flows): ${String(s.opportunityID ?? o?.Opportunity_Id ?? o?.OpportunityId ?? 'unknown')}`
    );
    lines.push(`Salesforce opportunity id (if known): ${String(o?.SalesForce_Id ?? sessionStorage.getItem('SalesForceId') ?? 'unknown')}`);
    lines.push(`Opportunity name: ${String(o?.Name ?? o?.OpportunityName ?? sessionStorage.getItem('opportunityName') ?? '—')}`);
    lines.push(`Stage / status (session hints): ${sessionStorage.getItem('currentStage') ?? '—'} / ${sessionStorage.getItem('stageStatus') ?? '—'}`);
    lines.push(`Site address (viewer): ${String(useViewerStore.getState().siteAddress || '—')}`);
    lines.push(`Utility company (session): ${sessionStorage.getItem('Utility_Company') ?? '—'}`);
    lines.push('');
    lines.push('## Heuristic “next step” (same as dashboard live guide)');
    lines.push(`Target area: ${guide.section}`);
    lines.push(guide.message);
    lines.push('');
    lines.push('## Financing selection');
    lines.push(
        `Selected grid cells (partner::product): ${Array.from(s.selectedFinanceCells).join(', ') || 'none'}`
    );
    lines.push(`Financing grid rows loaded: ${s.financingGrid?.length ?? 0}`);
    lines.push('');
    lines.push('## Finance partner sync (checklist)');
    lines.push(summarizeSync(s.financePartnerSyncStatus));
    lines.push('');
    lines.push('## Opportunity products (Salesforce-linked summary)');
    lines.push(summarizeOpportunityProduct(o?.OpportunityProduct ?? o?.opportunityProduct));
    lines.push('');
    lines.push('## Quote / contract summary (dashboard lines)');
    lines.push(`Selected quote line: ${s.dashboardQuotesSummaryLines?.selectedQuoteLine ?? '—'}`);
    lines.push(`Signed contract line: ${s.dashboardQuotesSummaryLines?.signedContractLine ?? '—'}`);
    lines.push('');
    lines.push('## Change order / loan status (Contract getLoanStatus cache)');
    lines.push(
        `Rows cached: ${s.changeOrderDetails?.length ?? 0}; active change-order flags: ${
            s.changeOrderDetails?.some((r) => r.Is_Change_Order === true || r.is_Change_Order === true)
                ? 'yes'
                : 'no'
        }`
    );
    lines.push('');
    lines.push(
        'If any field reads "unknown" or is missing, tell the user you do not see it in context and suggest where in the app they might find it (e.g. Opportunity card, Financing Options, Commerce hub).'
    );
    return lines.join('\n');
}
